import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ConfirmDialogComponent, Logger } from 'src/app/@shared';
import { FormControl, FormGroup } from '@angular/forms';
import { Store, VersionStoreService, VersionService, Version, VersionStoreDialogComponent } from '../..';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectionModel } from '@angular/cdk/collections';

const log = new Logger('VersionStoreListComponent');

@Component({
  selector: 'app-version-store-list',
  templateUrl: './version-store-list.component.html',
  styleUrls: ['./version-store-list.component.scss']
})

export class VersionStoreListComponent<T extends Store, TVersion extends Version> implements OnInit {
  @Input() versionId: string = '0';
  @Input() eventId = '';
  @Output() actionType = new EventEmitter<string>();
  @Output() closeDialog = new EventEmitter<string>();
  @Output() moveToNewVersion = new EventEmitter<any>();
  storesUpdated = false;

  viewModel$!: Observable<{
    stores: any[];
    isLoading: boolean;
    totalRecords: number;
    page: any;
    version: any;
  }>;

  storesList: T[] = [];
  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode'];
  selection = new SelectionModel<T>(true, []);

  constructor(
    private versionStoreService: VersionStoreService<T>,
    private mediaObserver: MediaObserver,
    private versionService: VersionService<TVersion>,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    log.debug('init');
    if (!this.versionId || this.versionId === '0') {
      return;
    }
    this.versionStoreService.versionId = this.versionId;

    this.clearSearch();
    this.versionStoreService.reload();

    this.viewModel$ = combineLatest([
      this.versionStoreService.stores$,
      this.versionStoreService.isLoading$,
      this.versionStoreService.totalRecords$,
      this.versionStoreService.page$,
      this.versionService.getVersion(this.versionId)
    ]).pipe(
      map(([stores, isLoading, totalRecords, page, version]) => {
        if (stores.length === 0 && page.pageIndex > 0) {
          page.previousPageIndex = 0;
          page.pageIndex = 0;
          this.versionStoreService.page(page);
          this.versionStoreService.reload();
        }
        return { stores, isLoading, totalRecords, page, version };
      })
    );

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'PostalCode'];
        }
        else {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode'];
        }
      });

    this.versionStoreService.stores$.subscribe((data) => {
      this.storesList = data;
    });
  }

  onAssociateStore() {
    const dialogRef = this.dialog.open(VersionStoreDialogComponent, {
      width: '80%',
      height: '90%',
      data: { versionId: this.versionId, eventId: this.eventId },
    });
    this.versionStoreService.dialogSearch('');

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.storesUpdated = true;
        this.selection.clear();
      } else {
        this.storesUpdated = false;
      }
      this.clearSearch();
      this.actionType.emit(this.storesUpdated ? 'updated' : 'cancelled');
    });
  }

  onSearch(event: any) {
    this.versionStoreService.search(event.target.value);
    this.selection.clear();
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.versionStoreService.search('');
  }

  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  onSort(sortState: Sort): void {
    this.versionStoreService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.versionStoreService.page(pageEvent);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.storesList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.storesList.forEach(row => this.selection.select(row));
  }

  onClose(): void {
    this.closeDialog.emit(this.storesUpdated ? 'updated' : 'cancelled');
  }

  onDelete() {
    const stores = this.selection.selected;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to delete ${stores.length > 1 ? 'these stores' : 'this store'} from this version?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(confirmResult => {
      if (confirmResult) {
        this.versionStoreService
          .disassociateVersionToStore(this.versionId, stores)
          .subscribe({
            next: () => {
              this.storesUpdated = true;
              this.actionType.emit(this.storesUpdated ? 'updated' : 'cancelled');
              this.matSnackBar.open('Stores deleted', 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.versionStoreService.reload();
              this.selection.clear();
            },
            error: (error) => {
              log.error('Error occurred while performing store action', error);
              throw new Error(error.error?.value || error.message);
            }
          });
      }
    });
  }

  onMoveToNewVersion() {
    if (this.selection.selected.length === 0) {
      return;
    }

    let payload = {
      versionId: this.versionId,
      stores: this.selection.selected
    };

    this.moveToNewVersion.emit(payload);
  }

}
